import * as React from 'react';
import { ChevronDown, History, Plus, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NewOrderButtonProps {
  /** Called when the main "New" segment is clicked. */
  onCreateOrder: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "New" button shown on the Orders screen for users with `orders:write`. The main segment always
 * opens the manual order creation drawer; the chevron/import menu segment always renders alongside
 * it. Renders nothing unless the user has `orders:write`.
 */
export function NewOrderButton({ onCreateOrder }: NewOrderButtonProps): React.JSX.Element | null {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  if (!hasPermission('orders:write')) {
    return null;
  }

  function handleImportClick(): void {
    void navigate({ to: '/imports' });
  }

  function handleImportHistoryClick(): void {
    void navigate({ to: '/imports/history' });
  }

  return (
    <div className="flex items-center" role="group">
      <Button className="rounded-e-none" onClick={onCreateOrder} type="button">
        <Plus className="h-4 w-4" />
        {t('newOrder.button')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={t('newOrder.menuAriaLabel')}
            className="rounded-s-none border-s border-primary-foreground/20 px-2"
            type="button"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleImportClick}>
            <Upload className="h-4 w-4" />
            {t('newOrder.menu.import')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleImportHistoryClick}>
            <History className="h-4 w-4" />
            {t('newOrder.menu.importHistory')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
